import React, { useEffect, useState, useCallback } from "react";
import { ShieldCheck, Plus, Pencil, Trash2, FileText, Download } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, API } from "../../lib/api";
import { TYPE_MAP, COL_MAP, COLUMNS, daysLabel } from "../../lib/assets";
import AssetModal from "../../components/AssetModal";
import { fmtDateGB } from "../../lib/format";

const TypeIcon = ({ type, size = 14, ...p }) => {
  const Icon = TYPE_MAP[type]?.icon || ShieldCheck;
  return <Icon size={size} {...p} />;
};

export default function ProjectAssets({ projectId }) {
  const [assets, setAssets] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/projects/${projectId}/assets`);
      setAssets(data.assets || []);
      setCounts(data.status_counts || {});
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load assets");
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const remove = async (a) => {
    if (!window.confirm(`Delete this ${TYPE_MAP[a.asset_type]?.label || "asset"}?`)) return;
    try {
      await api.delete(`/assets/${a.id}`);
      toast.success("Asset deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <section className="swiss-card p-6" data-testid="project-assets-panel">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="label flex items-center gap-2"><ShieldCheck size={12} /> Assets · {assets.length}</div>
          <div className="font-display text-xl font-semibold mt-1">Equipment & warranties on this site</div>
        </div>
        <button onClick={() => { setEditing(null); setModalOpen(true); }} className="btn-primary" data-testid="project-asset-add">
          <Plus size={14} /> Register asset
        </button>
      </div>

      {/* Status summary chips */}
      <div className="flex flex-wrap gap-2 mb-5" data-testid="project-asset-summary">
        {COLUMNS.map((c) => (
          <div key={c.key} className="flex items-center gap-2 px-3 py-1.5 rounded-full border text-[11px] font-semibold"
            style={{ borderColor: c.accent + "55", background: c.soft, color: c.accent }}
            data-testid={`project-asset-count-${c.key}`}>
            <span className="w-2 h-2 rounded-full" style={{ background: c.accent }} />
            {c.label}: {counts[c.key] || 0}
          </div>
        ))}
      </div>

      {loading ? (
        <div className="muted text-sm">Loading assets…</div>
      ) : assets.length === 0 ? (
        <div className="muted text-sm py-6 text-center border border-dashed border-[var(--line)] rounded-lg">
          No assets registered on this site yet. Register panels, inverters and more to track their warranties.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="project-asset-table">
            <thead>
              <tr className="text-left label border-b border-[var(--line)]">
                <th className="py-2.5 px-3">Type</th>
                <th className="py-2.5 px-3">Manufacturer / Model</th>
                <th className="py-2.5 px-3">Serial</th>
                <th className="py-2.5 px-3 text-right">Qty</th>
                <th className="py-2.5 px-3">Warranty end</th>
                <th className="py-2.5 px-3">Status</th>
                <th className="py-2.5 px-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {assets.map((a) => {
                const c = COL_MAP[a.warranty_status];
                return (
                  <tr key={a.id} className="border-b border-[var(--line)] hover:bg-[var(--bg-2)]" data-testid={`project-asset-row-${a.id}`}>
                    <td className="py-2 px-3"><span className="inline-flex items-center gap-1.5"><TypeIcon type={a.asset_type} /> {TYPE_MAP[a.asset_type]?.label || a.asset_type}</span></td>
                    <td className="py-2 px-3 font-medium">{a.manufacturer || "—"}{a.model ? <span className="muted font-normal"> · {a.model}</span> : null}</td>
                    <td className="py-2 px-3 font-mono text-xs">{a.serial_number || "—"}</td>
                    <td className="py-2 px-3 text-right font-mono">{a.quantity}</td>
                    <td className="py-2 px-3 font-mono text-xs">{fmtDateGB(a.warranty_end_date)}<div className="text-[10px] muted">{daysLabel(a.days_remaining)}</div></td>
                    <td className="py-2 px-3">
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full inline-flex items-center gap-1" style={{ background: c.soft, color: c.accent }}>
                        {c.label}{a.is_override ? " *" : ""}
                      </span>
                    </td>
                    <td className="py-2 px-3 text-right whitespace-nowrap">
                      {a.warranty_doc_id && (
                        <a href={`${API}/files/${a.warranty_doc_id}`} target="_blank" rel="noopener noreferrer"
                           className="btn-ghost p-1.5" title="Warranty certificate" data-testid={`project-asset-doc-${a.id}`}><FileText size={13} /></a>
                      )}
                      <button onClick={() => { setEditing(a); setModalOpen(true); }} className="btn-ghost p-1.5" data-testid={`project-asset-edit-${a.id}`}><Pencil size={13} /></button>
                      <button onClick={() => remove(a)} className="btn-ghost p-1.5 text-[#C0392B]" data-testid={`project-asset-delete-${a.id}`}><Trash2 size={13} /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <AssetModal open={modalOpen} onClose={() => setModalOpen(false)} onSaved={load}
        existing={editing} lockedProjectId={projectId} />
    </section>
  );
}
